/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import * as Yup from 'yup';
import { MultiStepFormCtx } from '../../providers/MultiStepForm.provider';

import type { FieldData, FieldDataMap, FieldUtils, SchemaType, Sections, Subscriptions, SubSection } from './types';
import { JSX } from 'react';

const fieldUtilsFactory = <T>(sections: Sections<T>): FieldUtils<T> => {
  function getInitValues(): T & { [key: string]: string | boolean } {
    const sectionValues = Object.values(sections);

    return sectionValues.reduce((existingFields, section) => {
      const fieldDataList = Object.values(flattenSection(section || [])) as FieldData<T>[];

      return {
        ...existingFields,
        ...fieldDataList.reduce(
          (existingSections, { fieldName, initValue }) => ({ ...existingSections, [fieldName]: initValue }),
          {},
        ),
      };
    }, {} as T & { [key: string]: string | boolean });
  }

  function getErrorElementsBySection(sectionId: string, ctx: MultiStepFormCtx<T>): JSX.Element[] {
    const section = getSection(sectionId);

    return section.reduce((acc, { fields }) => {
      const fieldDataList = Object.values(fields) as FieldData<T>[];

      return [
        ...acc,
        ...fieldDataList.filter((field) => field.dataType === 'error').map((field) => field.getElement(ctx)),
      ];
    }, [] as JSX.Element[]);
  }

  function getFieldNamesBySection(sectionId: string, ctx: MultiStepFormCtx<T>): (keyof T)[] {
    return getSectionFields(sectionId)
      .filter((field) => field.showField(ctx))
      .map((field) => field.fieldName);
  }

  function getResetSubscriptions(): Subscriptions<T> {
    const sectionValues = Object.values(sections);
    return sectionValues.reduce((prevSection, section) => {
      const fieldDataList = Object.values(flattenSection(section || [])) as FieldData<T>[];

      const filteredList = fieldDataList.filter((fieldData) => fieldData.resetOnUpdate);
      const newFilteredList = filteredList.reduce((prevSubscriptions, fieldData) => {
        const newSubscriptions = fieldData.resetOnUpdate?.reduce((acc, subscription) => {
          const { fieldName } = fieldData;
          const prevSubscribers = prevSubscriptions[subscription];
          const prevSectionSub = prevSection[subscription];
          if (prevSubscribers) {
            const updatedSubscribers = [...prevSubscribers, fieldName];
            return { ...acc, [subscription]: updatedSubscribers };
          }
          if (prevSectionSub) {
            const updatedSubscribers = [...prevSectionSub, fieldName];
            return { ...acc, [subscription]: updatedSubscribers };
          }

          const updatedSubscribers = [fieldName];
          return { ...acc, [subscription]: updatedSubscribers };
        }, {});
        return {
          ...prevSubscriptions,
          ...newSubscriptions,
        };
      }, {} as Subscriptions<T>);

      return {
        ...prevSection,
        ...newFilteredList,
      };
    }, {} as Subscriptions<T>);
  }

  function getRequiredFieldNamesBySection(sectionId: string, ctx: MultiStepFormCtx<T>): (keyof T)[] {
    return getSectionFields(sectionId)
      .filter((field) => field.showField(ctx))
      .filter((field) => field.getInputState(ctx) === 'required')
      .map((field) => field.fieldName);
  }

  function getSection(sectionId: string): SubSection<T>[] {
    return sections[sectionId] || [{ label: '', fields: {}, showSection: () => false }];
  }

  function getSectionIds(): string[] {
    return Object.keys(sections);
  }

  function getValidationSchema(ctx: MultiStepFormCtx<T>, initalValidationSchema?: { [key: string]: any }): SchemaType {
    const sectionValues = Object.values(sections);

    const validations = sectionValues.reduce(
      (existingFields, section) => {
        const fieldDataList = Object.values(flattenSection(section || [])) as FieldData<T>[];

        return {
          ...existingFields,
          ...fieldDataList
            .filter((fieldData) => fieldData.validate)
            .reduce(
              (existingValidations, fieldData) => ({
                ...existingValidations,
                [fieldData.fieldName]: fieldData.validate && fieldData.validate.call(fieldData, ctx),
              }),
              {},
            ),
        };
      },
      { ...initalValidationSchema },
    );

    return Yup.object().shape(validations);
  }

  function isRequiredField(sectionId: string, fieldName: keyof FieldDataMap<T>, ctx: MultiStepFormCtx<T>): boolean {
    const subsections = sections[sectionId] || ([{}] as SubSection<T>[]);
    const subSection = subsections.find((_subSection) => _subSection.fields[fieldName] !== undefined);

    if (subSection) {
      const field = subSection.fields[fieldName];
      return field ? field.getInputState(ctx) === 'required' : false;
    }

    return false;
  }

  function getCompletionPercentage(sectionId: string, ctx: MultiStepFormCtx<T>, atLeastCurrentStep = true) {
    const fields = getRequiredFieldNamesBySection(sectionId, ctx);
    if (fields.length === 0) return atLeastCurrentStep ? 100 : 0;

    const filled = fields.filter((field) => ctx.touched[field] && !ctx.errors[field]);
    return (filled.length / fields.length) * 100;
  }

  function hasError(sectionId: string, ctx: MultiStepFormCtx<T>) {
    const fields = getFieldNamesBySection(sectionId, ctx);
    const errorCount = fields.filter((field) => ctx.touched[field] && ctx.errors[field]).length;
    return errorCount > 0;
  }

  return {
    getCompletionPercentage,
    getInitValues,
    getErrorElementsBySection,
    getFieldNamesBySection,
    getRequiredFieldNamesBySection,
    getResetSubscriptions,
    getSection,
    getSectionIds,
    getValidationSchema,
    hasError,
    isRequiredField,
  };

  function getSectionFields(sectionId: string) {
    return Object.values(flattenSection(getSection(sectionId))) as FieldData<T>[];
  }

  function flattenSection(section: SubSection<T>[]): FieldDataMap<T> {
    return section.reduce((acc, curr) => ({ ...acc, ...curr.fields }), {} as FieldDataMap<T>);
  }
};

export default fieldUtilsFactory;
